<template>
  <div class="data-table-container">
    <TableLite
      :is-slot-mode="true"
      :is-loading="loading"
      :columns="columns"
      :rows="rows"
      :total="totalCount"
      :sortable="sortableConfig"
      :messages="messages"
      :row-classes="getRowClasses"
      @do-search="handleSearch"
      @row-clicked="handleRowClick"
      @is-finished="handleFinished"
    >
    <!-- shown only when tripple shift + 9 is pressed -->
    <template v-slot:debug="data">
        <td style="font-size: 10px; color: yellow;">
          <pre>{{ JSON.stringify(data.value, null, 2) }}</pre>  <!-- Or customize: e.g., data.value.debug || 'N/A' -->
        </td>
      </template>

    <!-- Slot-based column templates -->
      <template v-for="column in slotColumns" 
                :key="column.field"
                v-slot:[column.slotName]="data">
        <div v-if="column.slotName!='akce'">
          <component :is="column.component"
            v-bind="getComponentProps(column, data.value)"
            @action="handleAction"
          />
        </div>

        <div v-else class="btnFlex">
          <component :is="column.component"
            v-if="data.value.calc_akce!='---'"
            v-bind="getComponentProps(column, data.value)"
            @action="handleAction"
          />
          <component :is="column.component"
            v-if="shouldShowManualDokoncit(data.value)"
            :id="data.value.id"
            :action="MANUALNE_DOKONCIT"
            @action="handleAction"
          />
        </div>

      </template>
    </TableLite>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { markRaw } from 'vue'
import TableLite from 'vue3-table-lite'
import StatusRedGreen from './StatusRedGreenComponent.vue'
import ActionButton from './ActionButtonComponent.vue'
import ImgLink from './ImgLinkComponent.vue'

export default {
  name: 'DataTable',
  components: { TableLite },

  props: {
    loading: { type: Boolean, default: false },
    rows: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    columns: { type: Array, default: () => [] }
  },

  emits: ['search', 'row-click', 'action', 'finished'],

setup(props, { emit }) {
    // Reactive data - convert from data() function
    const MANUALNE_DOKONCIT = 'MANUALNE_DOKONCIT';
    const shouldShowManualDokoncit = (rowData) => {
        const status = rowData.calc_stav_zakazky;
        return ['Stale aktivni', 'SCHVALOVANI PRERUSENO', 'SCHVALENO_MB', 'NENI_UZAVRENO'].includes(status);
    };

    const slotColumns = ref([
     {
        field: 'statusNahrani',
        slotName: 'statusNahrani',
        component: markRaw(StatusRedGreen),
        props: ['id', 'status', 'tooltip'],
        propMappings: { // push specific db column to tooltip
            'tooltip': 'statusNahrani',
            'status': 'calc_statusNahrani'
        }
      },
      {
        field: 'shodaCeny',
        slotName: 'shodaCeny',
        component: markRaw(StatusRedGreen),
        props: ['tooltip', 'status'],
        propMappings: { // push specific db column to tooltip
            'tooltip': 'shodaCeny',
            'status': 'calc_shodaCeny'
        }
      },
      {
        field: 'stav_zakazky',
        slotName: 'stav_zakazky',
        component: markRaw(ActionButton),
        props: ['id', 'action', 'tooltip'],
        propMappings: { // push specific db column to tooltip
            'tooltip': 'statusFakturace',
            'action': 'calc_stav_zakazky'
        }
      },
      {
        field: 'akce',
        slotName: 'akce',
        component: markRaw(ActionButton),
        props: ['id', 'action','tooltip'],
        propMappings: {
            'tooltip': 'akce',
            'action' : 'calc_akce'
        }
      },
      {
        field: 'imgLink',
        slotName: 'imgLink',
        component: markRaw(ImgLink),
        props: ['id', 'trnID', 'action', 'imgLink', 'data'],
        propMappings: {
             'action': 'calc_akce'
        }
      }
    ])
    
    // Computed properties - convert from computed object
    const sortableConfig = computed(() => {
      return { order: "id", sort: "desc" }
    })

    const messages = computed(() => {
      return {
        pagingInfo: "Zobrazeno {0}-{1} z {2}",
        pageSizeChangeLabel: "Počet záznamů: ",
        gotoPageLabel: " Stránka: ",
        noDataAvailable: "Data nenalezena"
      }
    })

    // Methods - convert to regular functions
    const getRowClasses = (row) => {
      return row.worker_status === 'WRK:WIP' ? ['rowwrkwip'] :
             row.trnStatus === 'ERROR' ? ['rowwrkerr'] : []
    }

    const getComponentProps = (column, rowData) => {
      const props = {}
      column.props.forEach(prop => {
            const sourceField = column.propMappings?.[prop] || prop;
            props[prop] = rowData[sourceField] ? rowData[sourceField] : null;
            if(prop==='data') props.data = rowData;
      });
      //console.debug('###!!! => ',column.field, rowData.id, column.props, props)
      //console.debug('###!!! => ', rowData)
      return props
    }

    const handleSearch = (...args) => {
      emit('search', ...args)
    }

    const handleRowClick = (row) => {
      emit('row-click', row)
    }

    const handleAction = (actionData) => {
      emit('action', actionData)
    }

    const handleFinished = (...args) => {
      emit('finished', ...args)
    }

    return {
      slotColumns,
      sortableConfig,
      messages,
      getRowClasses,
      getComponentProps,
      handleSearch,
      handleRowClick,
      handleAction,
      handleFinished,
      MANUALNE_DOKONCIT,
      shouldShowManualDokoncit
    }
  }
}
</script>

<style scoped>
.data-table-container {
  width: 100%;
}

/* Vue Table Lite deep styles */
::v-deep(.vtl-table .vtl-thead .vtl-thead-th) {
  color: #fff;
}

::v-deep(.vtl-card) {
  width: 100%;
}

::v-deep(.vtl-card-body) {
  background-color: #333333;
}

::v-deep(.vtl-table td),
::v-deep(.vtl-table tr) {
  border: none;
  color: white;
}

::v-deep(.vtl-table-hover tbody tr):hover {
  color: red;
  background-color: #555555;
}

::v-deep(.vtl-paging-info) {
  color: lightblue;
  margin-top: 6px;
}

::v-deep(.vtl-paging-change-div) {
  margin-top: 6px;
}

::v-deep(.vtl-paging-count-label),
::v-deep(.vtl-paging-page-label) {
  color: lightblue;
}

::v-deep(dataTables_paginate) {
  margin-top: -6px;
}

::v-deep(vtl-paging-pagination-page-li) {
  font-weight: normal;
  font-size: 10px;
}

::v-deep(.vtl-paging-pagination-page-link) {
  padding: 0.3rem 0.5rem;
  border: none;
  font-weight: bold;
  font-size: 12px;
}

::v-deep(.rowwrkwip) {
  background: #215f5e !important;
}

::v-deep(.rowwrkerr) {
  background: #5c2121 !important;
}
::v-deep(.rowwrkwip) { background: #215f5e !important; }
::v-deep(.rowwrkerr) { background: #5c2121 !important; }

/* Status circle styles for child components */
::v-deep(.statusCircle) {
  width: 5px;
  height: 5px;
  font-size: 1px;
  border-radius: 50%;
  padding: 7px 7px;
}

::v-deep(.green) {
  color: #068337;
  background-color: #068337;
}

::v-deep(.red) {
  color: #f7403a;
  background-color: #903333;
}

::v-deep(.btnFlex) {
  display: flex;
  justify-content: space-between;
}

::v-deep(.btnFirstIcon) {
  margin-right: 20px;
}
</style>